import { Fragment, type KeyboardEvent, type ReactNode, useState } from 'react';
import Icon from './Icon.tsx';
import type { ListResult } from '../lib/useList.ts';

type AriaSort = 'none' | 'ascending' | 'descending';

export interface Column<T> {
  key: string;
  label: string;
  /** Drives the filter control and the cell alignment. */
  type?: 'text' | 'number' | 'date' | 'bool' | 'choice';
  /** Options for a choice filter: value -> label. */
  choices?: { value: string; label: string }[];
  /**
   * What the column means, on hovering its heading. For the sentence a
   * reader needs once and then never again -- which is worth saying, and
   * not worth a line of the toolbar for ever.
   */
  hint?: string;
  sortable?: boolean;
  filterable?: boolean;
  align?: 'left' | 'right';
  /** How the cell is drawn. Defaults to the raw value. */
  render?: (row: T) => ReactNode;
  /** Hidden on a phone. Use for columns that are context rather than content. */
  secondary?: boolean;
}

/**
 * One table for every list in the app: search, per-column filters, sorting,
 * paging and export.
 *
 * The filter row is a second header row rather than a panel above the table, so
 * a filter sits under the column it applies to and there is no guessing which
 * box belongs to which heading.
 */
export default function DataTable<T>({
  list, columns, rowKey, rowClass, toolbar, exportName, empty, onRowClick, viewFilters, below,
}: {
  list: ListResult<T>;
  columns: Column<T>[];
  rowKey: (row: T) => string | number;
  rowClass?: (row: T) => string;
  toolbar?: ReactNode;
  /** Base path for the export links; omitted means no export buttons. */
  exportName?: string;
  /**
   * Filter keys that belong to the view rather than to the person, such as a
   * list that shows live rows by default. They still filter; they just do not
   * read as something anybody chose.
   */
  viewFilters?: string[];
  empty?: string;
  onRowClick?: (row: T) => void;
  below?: (row: T) => ReactNode;
}) {
  const { rows, total, loading, error, state, pages, set, setFilter, clearFilters, qs } = list;
  // On a phone a whole row of filter boxes costs more than it gives, so it
  // starts folded away behind a count.
  const [filtersOpen, setFiltersOpen] = useState(false);

  /*
   * Filters the person set, as opposed to the view they are looking at.
   *
   * A screen whose default is "live only" carries that as a filter, but it
   * is not something anybody chose: counting it put a permanent "Clear (1)"
   * in the toolbar and turned an empty list into "nothing matches those
   * filters" on a system where nothing had been made yet.
   */
  const activeFilters = Object.entries(state.filters)
    .filter(([k, v]) => v.trim() && !(viewFilters ?? []).includes(k)).length;
  const from = total === 0 ? 0 : (state.page - 1) * state.pageSize + 1;
  const to = Math.min(state.page * state.pageSize, total);

  const sortOn = (c: Column<T>) => {
    if (c.sortable === false) return;
    const same = state.sort === c.key;
    set({ sort: c.key, dir: same && state.dir === 'asc' ? 'desc' : 'asc' });
  };

  return (
    <>
      <div className="panel-head">
        <span className="count">
          {loading ? 'loading…' : `${total} row${total === 1 ? '' : 's'}`}
        </span>
        <div className="spacer" />
        {toolbar}
        {exportName && (
          <>
            <a className="btn" download style={{ textDecoration: 'none' }}
               href={`${exportName}?${qs({ format: 'csv' })}`}
               title="Everything the filters match, not just this page">CSV</a>
            <a className="btn" download style={{ textDecoration: 'none' }}
               href={`${exportName}?${qs({ format: 'xlsx' })}`}
               title="Everything the filters match, not just this page">Excel</a>
          </>
        )}
        <input className="search" placeholder="Search"
               value={state.search}
               onChange={(e) => set({ search: e.target.value })} />
        <button className="icon-btn" title={filtersOpen ? 'Hide filters' : 'Filters'}
                aria-label="Filters" aria-pressed={filtersOpen}
                onClick={() => setFiltersOpen((v) => !v)}>
          <Icon name="sliders" size={15} />
        </button>
        {(activeFilters > 0 || state.search.trim()) && (
          <button className="btn" onClick={clearFilters}>
            Clear{activeFilters > 0 ? ` (${activeFilters})` : ''}
          </button>
        )}
      </div>

      {error && <div className="error" style={{ margin: 16 }}>{error}</div>}

      {!loading && rows.length === 0 ? (
        <div className="empty">
          {activeFilters > 0 || state.search.trim()
            ? 'Nothing matches those filters.'
            : empty ?? 'Nothing here yet.'}
        </div>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}
                      title={c.hint}
                      className={[
                        c.align === 'right' ? 'num' : '',
                        c.secondary ? 'col-secondary' : '',
                        c.sortable === false ? '' : 'sortable',
                      ].filter(Boolean).join(' ')}
                      onClick={() => sortOn(c)}
                      // Reachable by keyboard too: Tab to a heading, Enter or
                      // Space to sort, the same as clicking it.
                      {...(c.sortable === false ? {} : {
                        tabIndex: 0,
                        'aria-sort': (state.sort !== c.key ? 'none'
                          : state.dir === 'asc' ? 'ascending' : 'descending') as AriaSort,
                        onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); sortOn(c); }
                        },
                      })}>
                    {c.label}
                    {state.sort === c.key && (
                      <span className="sort-mark">{state.dir === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </th>
                ))}
              </tr>
              {filtersOpen && (
                <tr className="filter-row">
                  {columns.map((c) => (
                    <th key={c.key} className={c.secondary ? 'col-secondary' : ''}>
                      {c.filterable === false ? null : c.type === 'choice' ? (
                        <select value={state.filters[c.key] ?? ''}
                                onChange={(e) => setFilter(c.key, e.target.value)}>
                          <option value="">All</option>
                          {(c.choices ?? []).map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : c.type === 'bool' ? (
                        <select value={state.filters[c.key] ?? ''}
                                onChange={(e) => setFilter(c.key, e.target.value)}>
                          <option value="">All</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : (
                        <input type={c.type === 'date' ? 'date'
                                   : c.type === 'number' ? 'number' : 'text'}
                               value={state.filters[c.key] ?? ''}
                               onChange={(e) => setFilter(c.key, e.target.value)} />
                      )}
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={rowKey(r)}>
                <tr className={rowClass?.(r) ?? ''}
                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                    style={onRowClick ? { cursor: 'pointer' } : undefined}>
                  {columns.map((c) => (
                    <td key={c.key}
                        className={[
                          c.align === 'right' ? 'num' : '',
                          c.secondary ? 'col-secondary' : '',
                        ].filter(Boolean).join(' ')}>
                      {c.render
                        ? c.render(r)
                        : String((r as Record<string, unknown>)[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
                {below?.(r)}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pager">
        <span className="dim">
          {total === 0 ? 'No rows' : `${from}–${to} of ${total}`}
        </span>
        <div className="spacer" />
        <label className="dim" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Rows
          <select value={state.pageSize}
                  onChange={(e) => set({ pageSize: Number(e.target.value) })}>
            {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button className="btn" disabled={state.page <= 1}
                onClick={() => set({ page: 1 })} title="First">«</button>
        <button className="btn" disabled={state.page <= 1}
                onClick={() => set({ page: state.page - 1 })}>Prev</button>
        <span className="dim">{state.page} / {pages}</span>
        <button className="btn" disabled={state.page >= pages}
                onClick={() => set({ page: state.page + 1 })}>Next</button>
        <button className="btn" disabled={state.page >= pages}
                onClick={() => set({ page: pages })} title="Last">»</button>
      </div>
    </>
  );
}
